<template>
  <app-layout :state="state" header-type="landing">
    <template #header>
      <icon-link
        :href="route('funding.projects.index', state.funding_pot.id)"
        icon="arrow-thin-left-circle"
        class="icons-white"
      >
        Back to projects
      </icon-link>
      <h1>
        {{ projectTitle }}
        <small class="font-normal text-xs opacity-50 leading-none block">{{ state.project.title }}</small>
      </h1>
    </template>
    <app-header>
      <template #navigation>
        <ul v-if="tabs">
          <li
            v-for="(tab, index) in tabs"
            :key="index"
          >
            <a
              :href="tab.route"
              :target="tab.blank ? '_blank' : '_self'"
              :class="{
                'is-active': currentRoute.startsWith(tab.ref),
                'is-disabled': tab.disabled
              }"
            >
              {{ tab.name }}
            </a>
          </li>
          <li>
            <button
              class="block border-b-4 pb-2 leading-none border-grey-900 transition-all transition-normal focus:outline-none"
              :class="{
                'text-negative-500 hover:border-negative-500': !state.project
                  .terminated_at,
                'text-positive-500 hover:border-positive-500':
                  state.project.terminated_at
              }"
              @click="toggleTerminateProject"
            >
              {{ !state.project.terminated_at ? "Terminate" : "Reinstate" }}
            </button>
          </li>
        </ul>
      </template>
      <template #tertiary>
        <portal-target name="tertiary" slim />
      </template>
    </app-header>
    <project-notices :notices="notices" />
    <slot />
    <portal to="modals">
      <app-modal v-if="showTerminateModal" @close="showTerminateModal = false">
        <app-card class="is-borderless max-w-sm">
          <form tag="form" @submit.prevent="terminateProject()">
            <div
              class="border-b px-6 py-3  bg-negative-700 border-negative-900 rounded-t"
            >
              <h2 class="text-xl text-white">
                Please confirm termination
              </h2>
            </div>
            <div class="px-6 py-4">
              <p class="mb-2">
                Are you want to terminate
                <strong>{{ state.project.ref }}</strong> as a project?
              </p>
              <p class="mb-2">
                Please type the project's reference and a reason for termination
                in to the fields below.
              </p>
              <div class="my-2">
                <label
                  class="uppercase text-sm tracking-wide text-grey-800 block mb-1 font-bold"
                  for="confirm_reference"
                >
                  Confirm Reference Number
                </label>
                <input
                  id="confirm_reference"
                  v-model.trim="terminateForm.ref"
                  class="border px-4 py-3 block w-full focus:outline-none focus:shadow-outline"
                  name="confirm_reference"
                  type="text"
                  :placeholder="state.project.ref"
                />
              </div>
              <div class="my-2">
                <label
                  class="uppercase text-sm tracking-wide text-grey-800 block mb-1 font-bold"
                  for="confirm_reason"
                >
                  Reason for termination
                </label>
                <textarea
                  id="confirm_reason"
                  v-model.trim="terminateForm.reason"
                  class="border px-4 py-3 block w-full focus:outline-none focus:shadow-outline"
                  name="confirm_reason"
                  rows="6"
                ></textarea>
              </div>
            </div>
            <div
              class="border-t px-6 py-4 text-right flex items-center justify-end"
            >
              <div class="w-32 mr-2">
                <app-button
                  type="button"
                  colour="text"
                  @click="showTerminateModal = false"
                >
                  Cancel
                </app-button>
              </div>
              <div class="w-32">
                <button
                  class="button bg-negative-700 hover:bg-white border border-negative-700 hover:text-negative-700"
                  type="submit"
                  :disabled="!validTerminationForm"
                >
                  Confirm
                </button>
              </div>
            </div>
          </form>
        </app-card>
      </app-modal>
    </portal>
  </app-layout>
</template>
<script>
import { userProjectNameLookupMixin } from "../mixins/userProject.js";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

export default {
  name: "FundingProjectBase",
  mixins: [userProjectNameLookupMixin],
  data() {
    return {
      notices: [],
      showTerminateModal: false,
      terminateForm: {
        ref: null,
        reason: null
      }
    };
  },
  computed: {
    currentRoute() {
      return this.state.route;
    },
    tabs() {
      let tabsArray = [];
      const funding = this.state.funding_pot.id;
      const project = this.state.project.uuid;
      // Overview
      tabsArray.push({
        name: "Overview",
        ref: "funding.projects.show",
        route: route("funding.projects.show", { funding, project })
      });

      // Team
      tabsArray.push({
        name: "Members",
        ref: "funding.projects.members.index",
        route: route("funding.projects.members.index", { funding, project })
      });

      // Requests
      tabsArray.push({
        name: "Change Requests",
        ref: "funding.projects.request.index",
        route: route("funding.projects.request.index", { funding, project })
      });

      tabsArray.push({
        name: "Finance",
        ref: "funding.projects.finance",
        route: route("funding.projects.finance.index", { funding, project })
      });

      // Contracts
      tabsArray.push({
        name: "Contracts",
        ref: "funding.projects.contracts.index",
        route: route("funding.projects.contracts.index", { funding, project })
      });

      // Application
      tabsArray.push({
        name: "Application",
        blank: true,
        ref: "applications.view",
        route: route("applications.view", project)
      });

      return tabsArray;
    },
    validTerminationForm() {
      return (
        this.state.project.ref === this.terminateForm.ref &&
        this.terminateForm.reason?.length
      );
    },
    projectTitle() {
      const cp = this.state.current_partner?.company;
      if (cp && cp.name) {
        return `[${this.state.project.ref}] ${cp.name}`;
      }
      const lp = this.state.partners?.find(p => p.company_id === this.state.project.company_id);
      if (!lp || (!lp.company && !lp.company_name)) {
        return `Project ${this.state.project.ref}`;
      }
      return `[${this.state.project.ref}] ${lp.company?.name ?? lp.company_name}`;
    },
  },
  created() {
    window._project = this.state.project.uuid;
    this.$once("hook:beforeDestroy", () => {
      delete window._project;
    });

    this.setTerminatedNotice();
  },
  methods: {
    formatDate(date) {
      return format(parseISO(date), "do MMMM yyyy");
    },
    toggleTerminateProject() {
      if (!this.state.project.terminated_at) {
        return (this.showTerminateModal = true);
      }
      return this.reinstateProject();
    },
    terminateProject() {
      if (!this.validTerminationForm) return;
      axios
        .put(
          route("funding.projects.show", {
            funding: this.state.funding_pot.id,
            project: this.state.project.uuid
          }),
          {
            action: "terminate",
            reason: this.terminateForm.reason
          }
        )
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          alert(
            "There was a problem updating this project, please try again later."
          );
        });
    },
    reinstateProject() {
      if (
        confirm(
          "Are you sure you want to reinstate " + this.state.project.ref + "?"
        )
      ) {
        axios
          .put(
            route("funding.projects.show", {
              funding: this.state.funding_pot.id,
              project: this.state.project.uuid
            }),
            {
              action: "reinstate"
            }
          )
          .then(() => {
            window.location.reload();
          })
          .catch(() => {
            alert(
              "There was a problem updating this project, please try again later."
            );
          });
      }
    },
    setTerminatedNotice() {
      if (!this.state.project.terminated_at) return;
      return this.notices.push({
        type: "negative",
        label: "Project Terminated",
        content: `This project was terminated on ${this.formatDate(
          this.state.project.terminated_at
        )}. You can reinstate the project at anytime by click 'Reinstate'.`,
        action: {
          label: "Back To Projects",
          method: () =>
            Turbolinks.visit(
              route("funding.projects.index", {
                funding: this.state.funding_pot.id
              })
            )
        }
      });
    }
  }
};
</script>
